import { useSelector } from 'react-redux';
import { Box } from '@chakra-ui/react';
import { ApplicationState } from '../../../../../store';
import { CustomHeading } from 'shared-components';
import Field from '../../../../Field/Field';
import { CustomCollapse } from '../../../../CustomCollapse/CustomCollapse';
import { PrevNextButtons } from '../../../../PrevNextButtons';
import { FileList } from '../../../../FileList/FileList';

import { useNavigate } from 'react-router-dom';
import ApplicantHeader from '../../../../../components/molecules/applicantHeader';
import { ApplicationRefereeRelation, IReferee } from 'api-paths-cas';
import { SectionHeader, SectionTitles } from '@/components/molecules/SectionHeader';
import { getApplicationData } from '@/components/pages/applicant-dashboard/sections/helpers/getApplicationData';
import { combineUnevenArrays, formatAddress } from '../helpers';
import { useTaskRouteGuard } from '@/hooks/useTaskRouteGuard';
import { TaskLoadingSkeleton } from '@/components/molecules/TaskLoadingSkeleton';
import { useTaskNavigation } from '@/hooks/useTaskNavigation';

export default function References() {
  const { application, allDocuments } = useSelector((state: ApplicationState) => state.apply);

  // Route guard - redirects if task is disabled
  const { isLoading } = useTaskRouteGuard('/references');

  const navigate = useNavigate();
  const { nextRoute, previousRoute } = useTaskNavigation();

  if (isLoading) {
    return <TaskLoadingSkeleton />;
  }

  const { initial, current } = getApplicationData(application);

  const { references } = current;
  const { references: initialReferences } = initial;

  const combinedReferences = combineUnevenArrays(references, initialReferences);
  const showReferences = combinedReferences?.length > 0;

  const files = allDocuments?.referenceLetter || [];

  const getRefereeYearsKnownLabel = (referee: IReferee) => {
    const years = referee?.yearsKnown;
    if (!years) return undefined; // missing entries are treated as undefined
    // existing entries are then formatted based on year number
    return years > 0 ? `${years} ${years === 1 ? 'year' : 'years'}` : 'less than a year';
  };

  return (
    <>
      <ApplicantHeader pageName="Application" />
      <Box>
        <SectionHeader title={SectionTitles.REFERENCES} />

        <FileList files={files} />

        <Box>
          {!showReferences ? (
            <Box bgColor="utility.100" px={5} py={3} mt={3} color="muted">
              The applicant has no data for this task
            </Box>
          ) : (
            <>
              <CustomHeading level="h2" mb={2} mt={6} fontSize="md">
                Data
              </CustomHeading>
              {combinedReferences.map((referee, index) => {
                const isRelationshipOther =
                  referee.current?.relationship === ApplicationRefereeRelation.Other ||
                  referee.original?.relationship === ApplicationRefereeRelation.Other;
                return (
                  <CustomCollapse key={`${referee.current?.name ?? referee.original?.name}-${index}`} defaultIsOpen>
                    <CustomCollapse.Trigger>{`Referee ${
                      referee.current?.name ?? referee.original?.name
                    }`}</CustomCollapse.Trigger>
                    <CustomCollapse.Content>
                      <Field leftLabel="Name" value={referee.current?.name} originalValue={referee.original?.name} />
                      <Field
                        leftLabel="Relationship"
                        value={referee.current?.relationship}
                        originalValue={referee.original?.relationship}
                      />
                      {isRelationshipOther && (
                        <Field
                          leftLabel="Custom relationship"
                          value={referee.current?.otherRelationshipDetails}
                          originalValue={referee.original?.otherRelationshipDetails}
                        />
                      )}
                      <Field
                        leftLabel="Length of relationship"
                        value={getRefereeYearsKnownLabel(referee.current)}
                        originalValue={getRefereeYearsKnownLabel(referee.original)}
                      />
                      <Field
                        leftLabel="Occupation"
                        value={referee.current?.occupation}
                        originalValue={referee.original?.occupation}
                      />
                      <Field
                        leftLabel="Employer"
                        value={referee.current?.organisation}
                        originalValue={referee.original?.organisation}
                      />
                      <Field leftLabel="Email" value={referee.current?.email} originalValue={referee.original?.email} />
                      <Field
                        leftLabel="Phone number"
                        value={referee.current?.phone ? `+${referee.current?.phone}` : undefined}
                        originalValue={referee.original?.phone ? `+${referee.original?.phone}` : undefined}
                      />
                      <Field
                        leftLabel="Address"
                        value={formatAddress(referee.current)}
                        originalValue={formatAddress(referee.original)}
                      />
                    </CustomCollapse.Content>
                  </CustomCollapse>
                );
              })}
            </>
          )}
        </Box>

        <Box mt={6}>
          <PrevNextButtons
            onPrevious={previousRoute ? () => navigate(previousRoute) : undefined}
            onNext={nextRoute ? () => navigate(nextRoute) : undefined}
          />
        </Box>
      </Box>
    </>
  );
}
